import React, { useState, useEffect } from 'react';
import { createJob, fetchUsers, createCustomer, fetchChoices, getUserData } from '../api';
import useDuplicateCustomerCheck from '../customers/useDuplicateCustomerCheck';
import customerLabel from '../customers/customerLabel';
import './newjob.css';
import TireDisposalToggle from '../TireDisposalToggle';

const NewJob = ({ onJobCreated, onClose, initialData }) => {
  const { confirmNewCustomer, duplicatePrompt } = useDuplicateCustomerCheck();
  const [submissionError, setSubmissionError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The "Creating..." spinner hides while the duplicate-customer dialog waits on staff.
  const showCreating = isSubmitting && !duplicatePrompt;
  const fileInputRef = React.useRef(null);

  const [newJob, setNewJob] = useState(() => ({
    contact: initialData?.contact || '',
    customer: initialData?.customer_name || '',
    make: initialData?.make || '',
    quantity: initialData?.quantity ?? '',
    service: initialData?.service || '',
    status: initialData?.status || 'tires',
    date_promised: initialData?.date_promised || '',
    time_promised: initialData?.time_promised || '',
    picture: null,
    shop_notes: initialData?.shop_notes || '',
    customer_notes: initialData?.customer_notes || '',
    ro: initialData?.ro || '',
    po: initialData?.po || '',
    tire_disposal: !!initialData?.tire_disposal,
  }));

  // Customer search and selection state
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(
    initialData ? { id: initialData.customer, name: initialData.customer_name } : null
  );
  const [choices, setChoices] = useState({});

  useEffect(() => {
    const searchCustomers = async () => {
      if (newJob.customer.trim().length < 2) {
        setCustomerSearchResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const response = await fetchUsers(newJob.customer);
        setCustomerSearchResults(response.results || []);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setIsSearching(false);
      }
    };
    const debounceTimer = setTimeout(searchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [newJob.customer]);

  // Load choices and user data to get default contact
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const choicesData = await fetchChoices();
        setChoices(choicesData);

        if (!initialData) {
          const userData = await getUserData();
          if (userData.data.default_contact) {
            setNewJob(prev => ({
              ...prev,
              contact: userData.data.default_contact
            }));
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setNewJob(prev => ({
      ...prev,
      customer: customer.name, // for display only
    }));
    setShowDropdown(false);
  };

  // Compress image before upload - optimized for memory (especially Android)
  // Returns original file if compression would cause issues
  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      // Always resolve, never reject - prevents unhandled promise rejections
      try {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const img = new Image();
            let resolved = false; // Track if we've already resolved
            
            img.onload = () => {
              if (resolved) return; // Prevent multiple resolutions
              
              try {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                
                // Calculate new dimensions
                if (width > maxWidth) {
                  height = (height / width) * maxWidth;
                  width = maxWidth;
                }
                
                // Limit canvas size to prevent memory issues
                const maxCanvasSize = 4096; // Max canvas dimension
                if (width > maxCanvasSize || height > maxCanvasSize) {
                  const scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
                  width = width * scale;
                  height = height * scale;
                }
                
                canvas.width = width;
                canvas.height = height;
                
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                canvas.toBlob((blob) => {
                  if (resolved) return; // Prevent multiple resolutions
                  
                  // Clean up memory immediately (remove error handler first to prevent false errors)
                  try {
                    img.onerror = null; // Remove error handler before cleanup
                    img.onload = null; // Remove load handler
                    img.src = '';
                    canvas.width = 0;
                    canvas.height = 0;
                  } catch (cleanupError) {
                    // Ignore cleanup errors
                  }
                  
                  if (!blob) {
                    console.warn('Canvas toBlob returned null, using original file');
                    resolved = true;
                    resolve(file);
                    return;
                  }
                  
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolved = true;
                  resolve(compressedFile);
                }, 'image/jpeg', quality);
              } catch (error) {
                if (resolved) return;
                console.warn('Canvas operation failed, using original:', error);
                resolved = true;
                resolve(file);
              }
            };
            
            img.onerror = () => {
              if (resolved) return; // Ignore errors after resolution
              console.warn('Image load failed, using original');
              resolved = true;
              resolve(file);
            };
            
            img.src = e.target.result;
          } catch (error) {
            console.warn('Image processing error, using original:', error);
            resolve(file);
          }
        };
        
        reader.onerror = () => {
          console.warn('FileReader error, using original file');
          resolve(file);
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        // If compression fails entirely, use original file
        console.warn('Compression error, using original file:', error);
        resolve(file);
      }
    });
  };

  const handleChange = async (e) => {
    try {
      const { name, value, type, files } = e.target;
      if (type === 'file') {
        let file = files[0];
        
        // Compress image on selection (not on submit) to keep job creation fast
        // All errors are caught and handled gracefully - compression always resolves
        if (name === 'picture' && file && file.type.startsWith('image/')) {
          const fileSizeMB = file.size / 1024 / 1024;
          
          // Skip compression for very small files (already optimized)
          if (fileSizeMB < 0.5) {
            console.log(`📸 Small image (${fileSizeMB.toFixed(2)} MB) - skipping compression`);
          } else {
            // For large files, use more aggressive compression
            if (fileSizeMB > 10) {
              console.warn(`Large image detected: ${fileSizeMB.toFixed(2)} MB - using smaller compression`);
              file = await compressImage(file, 1280, 0.7);
            } else if (fileSizeMB > 5) {
              // Medium-large files: moderate compression
              console.log(`📸 Compressing medium image: ${fileSizeMB.toFixed(2)} MB`);
              file = await compressImage(file, 1600, 0.75);
            } else {
              // Normal files: standard compression
              console.log(`📸 Compressing image: ${fileSizeMB.toFixed(2)} MB`);
              file = await compressImage(file);
            }
            console.log(`✅ Compressed to: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
          }
        }
        
        setNewJob({
          ...newJob,
          [name]: file
        });
      } else {
        setNewJob({
          ...newJob,
          [name]: value,
        });
        if (name === 'customer') {
          setSelectedCustomer(null);
          setShowDropdown(true);
        }
      }
      setHasUnsavedChanges(true);
    } catch (error) {
      // Catch any unexpected errors to prevent modal from closing
      // Compression function always resolves, so this should rarely happen
      console.error('Error in handleChange:', error);
      setSubmissionError('An error occurred. Please try again.');
      // Don't throw - keep modal open
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only validate customer
    if (!newJob.customer) {
      setSubmissionError('Customer name is required.');
      return;
    }

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Reset submission error if validation passes
    setSubmissionError('');
    setIsSubmitting(true);

    try {
      const startTime = Date.now();
      console.log('🚀 Starting job creation...');
      
      let customerObj;
      if (selectedCustomer) {
        customerObj = selectedCustomer;
      } else {
        const choice = await confirmNewCustomer({ name: newJob.customer });
        if (!choice) return; // back to the form
        if (choice.action === 'use') {
          customerObj = choice.customer;
        } else {
          console.log('⏱️ Creating customer...');
          const customerStart = Date.now();
          customerObj = await createCustomer({ name: newJob.customer });
          console.log(`✅ Customer created in ${Date.now() - customerStart}ms`);
        }
        // Remember it, so retrying after a failed save doesn't ask (or create) again.
        setSelectedCustomer(customerObj);
        setNewJob(prev => ({ ...prev, customer: customerObj.name }));
      }

      // Image is already compressed from handleChange, just use it
      const formData = new FormData();
      Object.keys(newJob).forEach(key => {
        if (key === 'picture') {
          // Only append picture if it exists and is a File
          if (newJob[key] instanceof File) {
            formData.append(key, newJob[key]);
            console.log(`📸 Picture size: ${(newJob[key].size / 1024 / 1024).toFixed(2)} MB`);
          }
        } else if (key !== 'customer') {
          formData.append(key, newJob[key]);
        }
      });
      formData.append('customer', customerObj.id);

      console.log('⏱️ Uploading job data...');
      const uploadStart = Date.now();
      const createdJob = await createJob(formData);
      console.log(`✅ Job uploaded in ${Date.now() - uploadStart}ms`);
      console.log(`🎉 Total time: ${Date.now() - startTime}ms`);
      
      if (createdJob) {
        onJobCreated(createdJob);
        setNewJob({
          contact: '',
          customer: '',
          make: '',
          quantity: '',
          service: '',
          status: 'tires',
          date_promised: '',
          time_promised: '',
          picture: null,
          shop_notes: '',
          customer_notes: '',
          ro: '',
          po: '',
          tire_disposal: false,
        });
        setSelectedCustomer(null);
      }
    } catch (error) {
      console.error('Error:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Handle memory errors specifically (should rarely happen now with automatic compression)
      if (error.message && (error.message.includes('memory') || error.message.includes('Memory'))) {
        setSubmissionError('The photo is too large for your device. Please try taking a new photo or selecting a different one.');
      } else {
        // Show the actual error message from server
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to create job. Please try again.';
        setSubmissionError(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
  };

  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <form onSubmit={handleSubmit} className='job'>
      <div className="job-header-container">
        <h2 className='jobheader'>New Job</h2>
        <button type="button" className="close-button" onClick={handleClose}>×</button>
      </div>
      <div className='jobform'>
        <div className="jobtop">
          <div className='jobcontact'>
            <div className='contactcolumn'>
              {/* Contact */}
              <label className='labelcontact'>
                Contact:
                <select
                  className='inputscontact'
                  name="contact"
                  value={newJob.contact}
                  onChange={handleChange}
                >
                  <option value="">Select Contact</option>
                  {choices.contact && choices.contact
                    .sort((a, b) => a.order - b.order)
                    .map(choice => (
                      <option key={choice.value} value={choice.value}>
                        {choice.display_name}
                      </option>
                    ))}
                </select>
              </label>

              {/* Customer with search and dropdown */}
              <div className="customer-search-container">
                <label className='labelcontact'>
                  Customer:
                  <input
                    className='inputscontact'
                    type="text"
                    name="customer"
                    value={newJob.customer}
                    onChange={handleChange}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    placeholder="Search or enter new customer"
                    autoComplete="off"
                  />
                </label>
                {showDropdown && newJob.customer.length >= 2 && (
                  <div className="customer-dropdown">
                    {isSearching ? (
                      <div className="customer-dropdown-item">Searching...</div>
                    ) : customerSearchResults.length > 0 ? (
                      customerSearchResults.map((customer) => (
                        <div
                          key={customer.id}
                          className="customer-dropdown-item"
                          onClick={() => handleCustomerSelect(customer)}
                        >
                          {customerLabel(customer)}
                        </div>
                      ))
                    ) : (
                      <div className="customer-dropdown-item">No customers found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Make */}
            <label className='labelcontact'>
              Make:
              <input
                className='inputscontact'
                type="text"
                name="make"
                value={newJob.make}
                onChange={handleChange}
              />
            </label>
          </div>

          {/* Quantity */}
          <label className='labelimportantinfo'>
            Quantity:
            <input
              className='inputsprice'
              type="number"
              name="quantity"
              value={newJob.quantity}
              onChange={handleChange}
            />
          </label>

          {/* Tire Disposal */}
          <label className='labelimportantinfo'>
            Tire Disposal:
            <TireDisposalToggle
              boxClassName='inputsprice'
              checked={newJob.tire_disposal}
              onChange={(checked) => {
                setNewJob(prev => ({ ...prev, tire_disposal: checked }));
                setHasUnsavedChanges(true);
              }}
            />
          </label>

          {/* Date Promised */}
          <label className='labelimportantinfo'>
            Date Promised:
            <input
              className='inputsprice'
              type="date"
              name="date_promised"
              value={newJob.date_promised}
              onChange={handleChange}
            />
          </label>

          {/* Time Promised */}
          <label className='labelimportantinfo'>
            Time Promised:
            <input
              className='inputsprice'
              type="time"
              name="time_promised"
              value={newJob.time_promised}
              onChange={handleChange}
            />
          </label>

          {/* Status */}
          <label className='labelimportantinfo'>
            Status:
            <select
              className='inputsprice'
              name="status"
              value={newJob.status}
              onChange={handleChange}
            >
              {choices.status && choices.status
                .sort((a, b) => a.order - b.order)
                .map(choice => (
                  <option key={choice.value} value={choice.value}>
                    {choice.display_name}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <div className="jobdetailssmall">
          {/* Service */}
          <label className='labeldetails'>
            Service:
            <select
              className='inputsdetails'
              name="service"
              value={newJob.service}
              onChange={handleChange}
            >
              <option value="">Select Service</option>
              {choices.service && choices.service
                .sort((a, b) => a.order - b.order)
                .map(choice => (
                  <option key={choice.value} value={choice.value}>
                    {choice.display_name}
                  </option>
                ))}
            </select>
          </label>

          {/* RO Number */}
          <label className='labeldetails'>
            Repair Order #:
            <input
              className='inputsdetails'
              type="text"
              name="ro"
              value={newJob.ro}
              onChange={handleChange}
            />
          </label>

          {/* PO Number */}
          <label className='labeldetails'>
            Purchase Order #:
            <input
              className='inputsdetails'
              type="text"
              name="po"
              value={newJob.po}
              onChange={handleChange}
            />
          </label>

          {/* Picture Upload */}
          <label className='labeldetails'>
            Picture:
            <input
              ref={fileInputRef}
              className='inputsdetails'
              type="file"
              name="picture"
              accept="image/*"
              capture="environment"
              onChange={handleChange}
            />
          </label>
        </div>

        <div className='jobdetailslarge'>
          {/* Shop Notes */}
          <label className='labeldetailslarge'>
            Shop Notes:
            <textarea
              className='inputsdetailslarge'
              name="shop_notes"
              value={newJob.shop_notes}
              onChange={handleChange}
            />
          </label>
          {/* Customer Notes */}
          <label className='labeldetailslarge'>
            Customer Notes:
            <textarea
              className='inputsdetailslarge'
              name="customer_notes"
              value={newJob.customer_notes}
              onChange={handleChange}
            />
          </label>
        </div>
      </div>

      {/* Mobile floating camera button */}
      <button 
        type="button"
        className='mobilecamerabutton' 
        onClick={handleCameraClick}
        aria-label="Take Photo"
      >
        ⊕
      </button>

      <div className='savebuttonbanner'>
        {/* Error message */}
        <div className="error-container">
          {submissionError && (
            <div className="submissionErrorStyle">
              {submissionError}
            </div>
          )}
        </div>
        
        {/* Desktop buttons */}
        <div className="desktop-buttons">
          <button 
            className={`savebutton ${showCreating ? 'savebutton-loading' : ''}`} 
            type="submit"
            disabled={isSubmitting}
          >
            {showCreating ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </div>

        {/* Mobile buttons */}
        <div className="mobile-buttons">
          <button 
            className={`mobile-save-button ${showCreating ? 'mobile-save-button-loading' : ''}`} 
            type="submit"
            disabled={isSubmitting}
          >
            {showCreating ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : (
              'Create'
            )}
          </button>
        </div>
      </div>
      {showUnsavedWarning && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <div className="unsaved-changes-buttons">
              <button onClick={handleConfirmClose}>Leave</button>
              <button onClick={handleCancelClose}>Stay</button>
            </div>
          </div>
        </div>
      )}
      {duplicatePrompt}
    </form>
  );
};

export default NewJob;
